/**
 * The front door. Drawings on paper, the way work gets stuck to a fridge: each
 * room shows what it does rather than describing itself, so there is no card,
 * no icon badge and no subtitle to read.
 *
 * The shelf below holds every picture ever kept — there is no cap, and the
 * trash button puts a picture away rather than destroying it. Everything put
 * away sits in a drawer underneath, one tap from coming back.
 */

import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Archive, Download, Music, Music2, RotateCcw, Trash2, Volume2, VolumeX } from 'lucide-preact';
import type { Picture } from './lib/store.ts';
import type { ArtKind } from './ui/RoomArt.tsx';
import { CrayonRule, RoomArt } from './ui/RoomArt.tsx';
import { isMuted, play, setMuted } from './engine/audio.ts';
import { ambientWanted, setAmbient } from './engine/ambient.ts';
import { ConfirmSheet, ToyButton } from './ui/controls.tsx';
import { Companion } from './ui/Companion.tsx';

export type Room = 'doodle' | 'alive' | 'math' | 'patterns' | 'shapes' | 'build' | 'music';

interface Props {
  pictures: Picture[];
  busy: boolean;
  onOpen: (room: Room) => void;
  onReopen: (picture: Picture) => void;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
  onDeleteForever: (id: string) => void;
  onExport: () => void;
}

const ROOMS: Array<{ id: Room; name: string; art: ArtKind }> = [
  { id: 'alive', name: 'Make it move', art: 'move' },
  { id: 'math', name: 'Numbers', art: 'numbers' },
  { id: 'patterns', name: 'Patterns', art: 'patterns' },
  { id: 'shapes', name: 'Shapes', art: 'shapes' },
  { id: 'build', name: 'Build', art: 'build' },
  { id: 'music', name: 'Music', art: 'music' },
  { id: 'doodle', name: 'Draw', art: 'draw' },
];

const tiltAt = (i: number): string => `${(i % 2 === 0 ? -1 : 1) * (1.2 + (i % 3) * 0.6)}deg`;

export function Home(props: Props): JSX.Element {
  const [muted, setMutedState] = useState(() => isMuted());
  const [music, setMusicState] = useState(() => ambientWanted());
  const [showDrawer, setShowDrawer] = useState(false);
  const [askDelete, setAskDelete] = useState<string | null>(null);

  const shelf = props.pictures.filter((p) => p.archivedAt === null);
  const archived = props.pictures.filter((p) => p.archivedAt !== null);

  const toggleSound = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
    if (!next) play('sparkle', 0.6);
  };

  const toggleMusic = () => {
    const next = !music;
    setAmbient(next);
    setMusicState(next);
  };

  return (
    <div class="home">
      <div class="home__inner">
        <header class="wordmark">
          <div>
            <h1 class="wordmark__name">Imagine Play</h1>
            <CrayonRule />
          </div>
          <div class="wordmark__controls">
            <ToyButton
              round
              label={music ? 'Turn the music off' : 'Play some music'}
              hideLabel
              pressed={music}
              onClick={toggleMusic}
            >
              {music ? <Music size={22} strokeWidth={2.5} /> : <Music2 size={22} strokeWidth={2.5} />}
            </ToyButton>
            <ToyButton round label={muted ? 'Turn sound on' : 'Turn sound off'} hideLabel onClick={toggleSound}>
              {muted ? <VolumeX size={24} strokeWidth={2.5} /> : <Volume2 size={24} strokeWidth={2.5} />}
            </ToyButton>
          </div>
        </header>

        <nav class="rooms">
          {ROOMS.map((room, i) => (
            <button
              key={room.id}
              type="button"
              class="room"
              style={{ '--tilt': `${[-1.6, 1.1, -0.8, 1.4, -1.2, 0.9, -1.3][i]}deg` }}
              onClick={() => {
                play('pop', 0.6);
                props.onOpen(room.id);
              }}
            >
              <RoomArt kind={room.art} />
              <span class="room__name">{room.name}</span>
            </button>
          ))}
        </nav>

        {shelf.length > 0 && (
          <section class="gallery">
            <div class="gallery__head">
              <h2 class="gallery__title">Your pictures</h2>
              <button type="button" class="gallery__link" onClick={props.onExport} disabled={props.busy}>
                <Download size={16} strokeWidth={2.5} />
                <span>Save to my computer</span>
              </button>
            </div>
            <div class="gallery__row">
              {shelf.map((picture, i) => (
                <div key={picture.id} class="gallery__item" style={{ '--tilt': tiltAt(i) }}>
                  <button
                    type="button"
                    class="gallery__open"
                    onClick={() => {
                      play('pop', 0.5);
                      props.onReopen(picture);
                    }}
                  >
                    <img src={picture.thumb} alt="A picture you made. Tap to keep drawing on it" />
                  </button>
                  <button
                    type="button"
                    class="gallery__badge"
                    aria-label="Put this picture away"
                    onClick={() => {
                      play('pop', 0.4);
                      props.onArchive(picture.id);
                    }}
                  >
                    <Archive size={16} strokeWidth={2.5} />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {archived.length > 0 && (
          <section class="gallery gallery--away">
            <div class="gallery__head">
              <button type="button" class="gallery__link" onClick={() => setShowDrawer((v) => !v)}>
                <Archive size={16} strokeWidth={2.5} />
                <span>
                  Put away ({archived.length})
                </span>
              </button>
            </div>
            {showDrawer && (
              <div class="gallery__row">
                {archived.map((picture, i) => (
                  <div key={picture.id} class="gallery__item gallery__item--away" style={{ '--tilt': tiltAt(i) }}>
                    <img src={picture.thumb} alt="A picture you put away" />
                    <button
                      type="button"
                      class="gallery__badge"
                      aria-label="Bring this picture back"
                      onClick={() => {
                        play('sparkle', 0.5);
                        props.onRestore(picture.id);
                      }}
                    >
                      <RotateCcw size={16} strokeWidth={2.5} />
                    </button>
                    <button
                      type="button"
                      class="gallery__badge gallery__badge--danger"
                      aria-label="Delete this picture forever"
                      onClick={() => setAskDelete(picture.id)}
                    >
                      <Trash2 size={16} strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      <Companion kept={shelf.length} />

      {askDelete && (
        <ConfirmSheet
          title="Delete this picture forever? It cannot come back."
          confirmLabel="Delete forever"
          onConfirm={() => {
            props.onDeleteForever(askDelete);
            setAskDelete(null);
          }}
          onCancel={() => setAskDelete(null)}
        />
      )}
    </div>
  );
}
